import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GardenResponse } from '../products/logo-garden/logo-garden.interfaces';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class LogoGardenService {
  private readonly BASE_URL = 'https://api.nvidia.partners';


  constructor(
    private http: HttpClient,
    private route: ActivatedRoute
  ) {}

  private getUrlSuffixForGpus(gpus: string[], locale: string): string {
    // Get category from URL path
    const category = window.location.pathname.split('/')[3]?.toLowerCase();

    // Only apply URL suffix logic for en-us locale and valid categories
    if (locale !== 'en-us' || (category !== 'graphics-cards' && 
        !(category === 'gaming-desktops' && gpus.length === 1))) {
      return '';
    }

    // Normalize GPU names by removing spaces and converting to uppercase
    const normalizedGpus = gpus.map(gpu => gpu.replace(/%20/g, ' ').toUpperCase().trim());

    // For gaming-desktop category, only continue if single RTX 5070
    if (category === 'gaming-desktops') {
      if (normalizedGpus[0] !== 'RTX 5070') {
        return '';
      }
    }

    // Check for specific single GPU cases
    if (normalizedGpus.length === 1) {
      const gpu = normalizedGpus[0];
      if (gpu === 'RTX 5090') return 'rtx-5090/';
      if (gpu === 'RTX 5080') return 'rtx-5080/';
      if (gpu === 'RTX 5070') return 'rtx-5070/';
      if (gpu === 'RTX 5060') return '';
      if (gpu === 'RTX 5060 TI') return '';
      if (gpu === 'RTX 5070 TI') return 'rtx-5070-family/';
    }

    // Special case for 5070 family (both Ti and non-Ti)
    if (normalizedGpus.length === 2 && 
        normalizedGpus.indexOf('RTX 5070 TI') !== -1 && 
        normalizedGpus.indexOf('RTX 5070') !== -1) {
      return 'rtx-5070-family/';
    }

    // Default to base URL for any other combination of multiple GPUs
    return '';
  }

  getGardenData(): Observable<GardenResponse> {
    const locale = window.location.pathname.split('/')[1];
    const category = window.location.pathname.split('/')[3]?.toLowerCase();
    const gpuParams = this.route.snapshot.queryParamMap.get('gpu');
    const gpus = gpuParams ? gpuParams.split(',') : [];
    const urlSuffix = this.getUrlSuffixForGpus(gpus, locale);
    
    let baseUrl = `https://www.nvidia.com/${locale}/geforce/graphics-cards/50-series/${urlSuffix}`;
    
    // Override URL for gaming laptops in allowed locales
    const allowedLaptopLocales = ['es-la', 'en-us', 'de-de', 'da-dk', 'es-es', 'fr-fr', 'pl-pl', 'en-gb', 'it-it','en-in','en-my','en-sg','en-au','en-ph','zh-tw','th-th','vi-vn','ko-kr','ja-jp','id-id'];
    if (allowedLaptopLocales.indexOf(locale) !== -1 && category === 'gaming-laptops') {
      baseUrl = `https://www.nvidia.com/${locale}/geforce/laptops/50-series/`;
    }
    else if (locale === 'en-us' && category === 'studio-laptops') {
      baseUrl = `https://marketplace.nvidia.com/en-us/consumer/studio-laptops/`;
    }
    
    const url = `${this.BASE_URL}/products/v1/where-to-buy?url=${baseUrl}`;
    
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(response => {
        try {
          const innerJson = JSON.parse(response);
          const parsedData = typeof innerJson === 'string' ? JSON.parse(innerJson) : innerJson;
          
          if (!parsedData || !parsedData.result) {
            throw new Error('Invalid response structure');
          }

          return parsedData;
        } catch (error) {
          console.error('Error parsing garden data:', error);
          throw error;
        }
      })
    );
  }
} 